import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.*
import kotlin.math.sqrt

/** Hra spárovaná s kompletním zápisem. Frameové statistiky se počítají jen z těchto her. */
data class DetailedGame(val game: StatGame, val sheet: PlayerSheetDto)

data class FramePerformance(
    val frame: Int,
    val resolved: Int,
    val strikes: Int,
    val spareOpportunities: Int,
    val spares: Int,
    val opens: Int,
)

data class BowlingMetrics(
    val games: Int,
    val firstBallAttempts: Int,
    val resolvedFrames: Int,
    val strikes: Int,
    val spareOpportunities: Int,
    val spares: Int,
    val opens: Int,
    val splits: Int,
    val splitSpareOpportunities: Int,
    val convertedSplits: Int,
    val firstBallAverage: Double?,
    val cleanGames: Int,
    val longestStrikeRun: Int,
    val doubles: Int,
    val turkeys: Int,
    val byFrame: List<FramePerformance>,
)

private data class FrameOutcome(
    val resolved: Boolean,
    val strike: Boolean,
    val spareOpportunity: Boolean,
    val spare: Boolean,
    val open: Boolean,
    val split: Boolean,
    val firstBall: Int?,
)

private fun frameOutcome(type: String, frame: FrameDto): FrameOutcome {
    val first = frame.t.firstOrNull()
    val strike = first != null && Scoring.isStrike(type, first)
    val second = frame.t.getOrNull(1)
    val spareOpportunity = first != null && !strike && second != null
    val spare = spareOpportunity && Scoring.isSpare(type, first!!, second!!)
    val resolved = strike || second != null
    return FrameOutcome(
        resolved = resolved,
        strike = strike,
        spareOpportunity = spareOpportunity,
        spare = spare,
        open = resolved && !strike && !spare,
        split = frame.split,
        firstBall = first,
    )
}

fun bowlingMetrics(details: List<DetailedGame>): BowlingMetrics {
    val perGame = details.map { d -> d.sheet.frames.take(10).map { frameOutcome(d.game.gameType, it) } }
    val all = perGame.flatten()
    var longest = 0
    var doubles = 0
    var turkeys = 0
    perGame.forEach { frames ->
        var run = 0
        frames.forEach { f ->
            if (f.strike) {
                run++
                if (run >= 2) doubles++
                if (run >= 3) turkeys++
                if (run > longest) longest = run
            } else run = 0
        }
    }
    val byFrame = (0 until 10).map { index ->
        val fs = perGame.mapNotNull { it.getOrNull(index) }
        FramePerformance(
            frame = index + 1,
            resolved = fs.count { it.resolved },
            strikes = fs.count { it.strike },
            spareOpportunities = fs.count { it.spareOpportunity },
            spares = fs.count { it.spare },
            opens = fs.count { it.open },
        )
    }
    return BowlingMetrics(
        games = details.size,
        firstBallAttempts = all.count { it.firstBall != null },
        resolvedFrames = all.count { it.resolved },
        strikes = all.count { it.strike },
        spareOpportunities = all.count { it.spareOpportunity },
        spares = all.count { it.spare },
        opens = all.count { it.open },
        splits = all.count { it.split && !it.strike },
        splitSpareOpportunities = all.count { it.split && it.spareOpportunity },
        convertedSplits = all.count { it.split && it.spare },
        firstBallAverage = all.mapNotNull { it.firstBall }.takeIf { it.isNotEmpty() }?.average()?.let(::round1),
        cleanGames = perGame.count { frames -> frames.size == 10 && frames.all { it.resolved && !it.open } },
        longestStrikeRun = longest,
        doubles = doubles,
        turkeys = turkeys,
        byFrame = byFrame,
    )
}

fun percentage(part: Int, whole: Int): String =
    if (whole <= 0) "–" else "${round1(part * 100.0 / whole)} %"

fun standardDeviation(scores: List<Int>): Double? {
    if (scores.isEmpty()) return null
    val avg = scores.average()
    return round1(sqrt(scores.map { (it - avg) * (it - avg) }.average()))
}

fun median(scores: List<Int>): Double? {
    if (scores.isEmpty()) return null
    val sorted = scores.sorted()
    val middle = sorted.size / 2
    return if (sorted.size % 2 == 1) sorted[middle].toDouble()
    else round1((sorted[middle - 1] + sorted[middle]) / 2.0)
}

fun bestThreeGameSeries(games: List<StatGame>): Int? = games
    .groupBy { "${it.playerId}:${it.sessionKey}" }
    .values
    .filter { it.size >= 3 }
    .maxOfOrNull { session -> session.sortedBy { it.gameNo }.take(3).sumOf { it.score } }

fun rollingAveragePoints(games: List<StatGame>, window: Int = 5): List<TimePoint> {
    val sorted = games.filter { it.date != null }
        .sortedWith(compareBy<StatGame> { it.date }.thenBy { it.sessionKey }.thenBy { it.gameNo })
    return sorted.mapIndexed { index, game ->
        val from = (index - window + 1).coerceAtLeast(0)
        val sample = sorted.subList(from, index + 1)
        TimePoint(game.date!!, round1(sample.map { it.score }.average()), game.source, "posledních ${sample.size} her")
    }
}

fun scoreDistribution(games: List<StatGame>): List<Pair<String, Int>> = games
    .groupingBy {
        val from = (it.score / 20) * 20
        val to = if (from >= 300) from else from + 19
        "$from–$to"
    }
    .eachCount()
    .entries
    .sortedBy { it.key.substringBefore('–').toIntOrNull() ?: 0 }
    .map { it.key to it.value }

@Composable
fun StatsTabs(selected: String, tabs: List<Pair<String, String>>, onSelect: (String) -> Unit) {
    Div(attrs = { classes("stats-tabs") }) {
        tabs.forEach { (id, label) ->
            Button(attrs = {
                classes(*(listOfNotNull("small-btn", "small-btn-on".takeIf { selected == id }).toTypedArray()))
                onClick { onSelect(id) }
            }) { Text(label) }
        }
    }
}

@Composable
fun CoverageNote(detailedGames: Int, allGames: Int) {
    val pct = percentage(detailedGames, allGames)
    P(attrs = { classes("muted", "coverage-note") }) {
        Text("Frameové statistiky: $detailedGames z $allGames her ($pct pokrytí).")
    }
}

@Composable
fun FrameStatsPanel(metrics: BowlingMetrics, detailedGames: Int, allGames: Int, season: BowlingMetrics? = null) {
    CoverageNote(detailedGames, allGames)
    if (detailedGames == 0) {
        P(attrs = { classes("muted") }) { Text("Pro zvolený filtr zatím nejsou uložené kompletní zápisy.") }
        return
    }
    Div(attrs = { classes("career-stats") }) {
        StatPill2(percentage(metrics.strikes, metrics.firstBallAttempts), "strike")
        StatPill2(percentage(metrics.spares, metrics.spareOpportunities), "spare")
        StatPill2(percentage(metrics.opens, metrics.resolvedFrames), "open frame")
        StatPill2(percentage(metrics.convertedSplits, metrics.splitSpareOpportunities), "dohozené splity")
        StatPill2(metrics.firstBallAverage?.toString() ?: "–", "1. hod Ø")
        StatPill2("${metrics.cleanGames}", "čistých her")
    }
    H4 { Text("Výsledek jednotlivých hodů") }
    ThrowOutcomeChart(metrics)
    P(attrs = { classes("muted") }) {
        Text("U 1. hodu znamená Open běžnou situaci bez striku a splitu; u 2. hodu neproměněný dohoz.")
    }
    H4 { Text("Série striků") }
    Div(attrs = { classes("career-stats") }) {
        StatPill2("${metrics.longestStrikeRun}", "nejdelší série")
        StatPill2("${metrics.doubles}", "doubly")
        StatPill2("${metrics.turkeys}", "turkey")
    }
    val samples = metrics.byFrame.map { it.resolved }.distinct()
    val sampleLabel = if (samples.size == 1) {
        val count = samples.first()
        "$count ${when (count) { 1 -> "hra"; in 2..4 -> "hry"; else -> "her" }}"
    } else "${samples.minOrNull()}–${samples.maxOrNull()} her podle dokončených framů"
    H4 { Text("Výkon podle framu · vzorek: $sampleLabel") }
    Div(attrs = { classes("stats-table-wrap") }) {
        Table(attrs = { classes("stats-table") }) {
            Thead { Tr { Th { Text("Frame") }; Th { Text("Strike") }; Th { Text("Spare") }; Th { Text("Open") } } }
            Tbody {
                metrics.byFrame.forEach { f ->
                    val baseline = season?.takeIf { it.games > 0 }?.byFrame?.firstOrNull { it.frame == f.frame }
                    Tr {
                        Td { Text("${f.frame}.") }
                        FrameValueCell(f.strikes, f.resolved, metrics.byFrame.map { it.strikes to it.resolved }, baseline = baseline?.let { it.strikes to it.resolved })
                        FrameValueCell(f.spares, f.spareOpportunities, metrics.byFrame.map { it.spares to it.spareOpportunities }, baseline = baseline?.let { it.spares to it.spareOpportunities })
                        FrameValueCell(f.opens, f.resolved, metrics.byFrame.map { it.opens to it.resolved }, lowerIsBetter = true, baseline = baseline?.let { it.opens to it.resolved })
                    }
                }
            }
        }
    }
    if (season != null && season.games > 0) P(attrs = { classes("muted") }) { Text("V závorkách je rozdíl proti stejnému framu za sezónu v procentních bodech.") }
    P(attrs = { classes("muted") }) { Text("Zeleně ▲ nejlepší, červeně ▼ nejhorší hodnoty ve sloupci (včetně shod); také odchylky alespoň 10 procentních bodů od celkového průměru. U Open je nižší hodnota lepší. Spare se počítá jen z příležitostí k dohozu.") }
    P(attrs = { classes("muted") }) { Text("U 10. framu se procenta vztahují k prvnímu hodu framu; bonusové hody nejsou samostatné framy.") }
}

internal fun frameValueHighlight(part: Int, total: Int, samples: List<Pair<Int, Int>>, lowerIsBetter: Boolean = false): String? {
    if (total == 0) return null
    val values = samples.filter { it.second > 0 }.map { it.first.toDouble() / it.second }
    if (values.isEmpty() || values.maxOrNull() == values.minOrNull()) return null
    val value = part.toDouble() / total
    val average = samples.sumOf { it.first }.toDouble() / samples.sumOf { it.second }
    val high = value == values.maxOrNull() || value - average >= 0.10 - 1e-9
    val low = value == values.minOrNull() || average - value >= 0.10 - 1e-9
    return when {
        high -> if (lowerIsBetter) "frame-worst" else "frame-best"
        low -> if (lowerIsBetter) "frame-best" else "frame-worst"
        else -> null
    }
}

@Composable
private fun FrameValueCell(part: Int, total: Int, samples: List<Pair<Int, Int>>, lowerIsBetter: Boolean = false, baseline: Pair<Int, Int>? = null) {
    val highlight = frameValueHighlight(part, total, samples, lowerIsBetter)
    Td(attrs = {
        highlight?.let { classes(it) }
        attr("title", "$part z $total · průměr ${percentage(samples.sumOf { it.first }, samples.sumOf { it.second })}")
    }) {
        Text("${if (highlight == "frame-best") "▲ " else if (highlight == "frame-worst") "▼ " else ""}${percentage(part, total)}")
        if (baseline != null && baseline.second > 0 && total > 0) {
            Text(" (${signedDifference(part * 100.0 / total, baseline.first * 100.0 / baseline.second)})")
        }
    }
}

private data class ThrowSegment(val label: String, val count: Int, val color: String)

@Composable
private fun ThrowOutcomeChart(metrics: BowlingMetrics) {
    val firstOther = (metrics.firstBallAttempts - metrics.strikes - metrics.splits).coerceAtLeast(0)
    val regularSpares = (metrics.spares - metrics.convertedSplits).coerceAtLeast(0)
    val splitOpen = (metrics.splitSpareOpportunities - metrics.convertedSplits).coerceAtLeast(0)
    val regularOpen = (metrics.spareOpportunities - metrics.spares - splitOpen).coerceAtLeast(0)
    ThrowOutcomeRow(
        label = "1. hod",
        segments = listOf(
            ThrowSegment("Strike", metrics.strikes, "#f5b301"),
            ThrowSegment("Split", metrics.splits, "#e8756b"),
            ThrowSegment("Open", firstOther, "#4a9de0"),
        ),
    )
    ThrowOutcomeRow(
        label = "2. hod",
        segments = listOf(
            ThrowSegment("Spare", regularSpares, "#3fb984"),
            ThrowSegment("Spare ze splitu", metrics.convertedSplits, "#b98cd8"),
            ThrowSegment("Open", regularOpen, "#e0655f"),
            ThrowSegment("Open ze splitu", splitOpen, "#d86fa0"),
        ),
    )
}

@Composable
private fun ThrowOutcomeRow(label: String, segments: List<ThrowSegment>) {
    val total = segments.sumOf { it.count }
    Div(attrs = { classes("throw-outcome-row") }) {
        Div(attrs = { classes("throw-outcome-label") }) { Text(label) }
        if (total == 0) {
            Div(attrs = { classes("throw-outcome-empty") }) { Text("Bez dat") }
        } else {
            Div(attrs = { classes("throw-outcome-bar") }) {
                segments.filter { it.count > 0 }.forEach { segment ->
                    val pct = segment.count * 100.0 / total
                    Div(attrs = {
                        classes("throw-outcome-segment")
                        attr("style", "width:$pct%;background:${segment.color}")
                        attr("title", "${segment.label}: ${round1(pct)} % (${segment.count})")
                    }) {
                        if (pct >= 12) Text("${round1(pct)} %")
                    }
                }
            }
            Div(attrs = { classes("throw-outcome-legend") }) {
                segments.forEach { segment ->
                    Span(attrs = { classes("legend-item") }) {
                        Span(attrs = { classes("legend-dot"); attr("style", "background:${segment.color}") }) {}
                        Text("${segment.label} ${percentage(segment.count, total)}")
                    }
                }
            }
        }
    }
}

@Composable
fun FormStatsPanel(games: List<StatGame>, seriesLabel: String = "nejlepší 3 hry") {
    val scores = games.map { it.score }
    val sd = standardDeviation(scores)
    Div(attrs = { classes("career-stats") }) {
        StatPill2(median(scores)?.toString() ?: "–", "medián")
        StatPill2(sd?.toString() ?: "–", "odchylka")
        StatPill2("${scores.count { it >= 200 }}", "her 200+")
        StatPill2(bestThreeGameSeries(games)?.toString() ?: "–", seriesLabel)
    }
    P(attrs = { classes("muted") }) {
        Text(if (sd == null) "" else if (sd <= 15) "Velmi stabilní výkony." else if (sd <= 25) "Středně stabilní výkony." else "Výkony mají větší rozptyl.")
    }
    H4 { Text("Klouzavý průměr (posledních 5 her · podle pořadí her)") }
    TimeChart(rollingAveragePoints(games), evenlySpaced = true)
    H4 { Text("Rozdělení náhozů") }
    DistributionChart(scoreDistribution(games), games.size)
}

@Composable
fun SessionComparisonPanel(games: List<StatGame>) {
    val gameNumbers = games.map { it.gameNo }.distinct().sorted()
    val sessions = games.groupBy { it.sessionKey }
        .values
        .sortedByDescending { session -> session.firstOrNull()?.date ?: "" }

    H4 { Text("Průměr podle pořadí hry") }
    BarChart(gameNumbers.mapNotNull { gameNo ->
        val sample = games.filter { it.gameNo == gameNo }
        if (sample.isEmpty()) null
        else Triple("$gameNo. hra", round1(sample.map { it.score }.average()), sample.size)
    })

    H4 { Text("Průběh jednotlivých zápasů a tréninků") }
    Div(attrs = { classes("stats-table-wrap") }) {
        Table(attrs = { classes("stats-table", "session-stats-table") }) {
            Thead {
                Tr {
                    Th { Text("Session") }
                    gameNumbers.forEach { Th { Text("$it. hra") } }
                    Th { Text("Průměr") }
                    Th { Text("Rozdíl") }
                }
            }
            Tbody {
                sessions.forEach { session ->
                    val sorted = session.sortedBy { it.gameNo }
                    val first = sorted.first()
                    val scoreByGame = sorted.associate { it.gameNo to it.score }
                    val firstScore = sorted.firstOrNull()?.score
                    val lastScore = sorted.lastOrNull()?.score
                    val difference = if (firstScore != null && lastScore != null && sorted.size > 1) lastScore - firstScore else null
                    val sessionLabel = buildList {
                        first.date?.let { add(it) }
                        add(sourceLabel(first.source))
                        if (first.source == "league" && first.round != null) add("${first.round}. kolo")
                    }.joinToString(" · ")
                    Tr {
                        Td { Text(sessionLabel) }
                        gameNumbers.forEach { gameNo -> Td { Text(scoreByGame[gameNo]?.toString() ?: "–") } }
                        Td { Text(round1(sorted.map { it.score }.average()).toString()) }
                        Td(attrs = {
                            difference?.let { classes(if (it > 0) "trend-up" else if (it < 0) "trend-down" else "trend-flat") }
                        }) {
                            Text(difference?.let { if (it > 0) "+$it" else "$it" } ?: "–")
                        }
                    }
                }
            }
        }
    }
    P(attrs = { classes("muted") }) { Text("Rozdíl porovnává poslední a první zaznamenanou hru dané session.") }
}

@Composable
private fun DistributionChart(rows: List<Pair<String, Int>>, total: Int) {
    val max = rows.maxOfOrNull { it.second }?.coerceAtLeast(1) ?: return
    Div(attrs = { classes("bar-chart") }) {
        rows.forEach { (label, count) ->
            Div(attrs = { classes("bar-row") }) {
                Div(attrs = { classes("bar-label") }) { Text(label) }
                Div(attrs = { classes("bar-track") }) {
                    val width = (count * 100 / max).coerceIn(6, 100)
                    Div(attrs = { classes("bar-fill"); attr("style", "width:$width%") }) { Text("$count") }
                }
                Div(attrs = { classes("bar-count") }) { Text(percentage(count, total)) }
            }
        }
    }
}

@Composable
fun PlayerProfileSummary(metrics: BowlingMetrics) {
    if (metrics.games == 0) return
    val strengths = buildList {
        if (metrics.firstBallAttempts > 0 && metrics.strikes * 100.0 / metrics.firstBallAttempts >= 40) add("vysoká strike úspěšnost")
        if (metrics.spareOpportunities > 0 && metrics.spares * 100.0 / metrics.spareOpportunities >= 70) add("spolehlivé dohozy")
        if (metrics.resolvedFrames > 0 && metrics.opens * 100.0 / metrics.resolvedFrames <= 15) add("málo otevřených framů")
    }
    val focus = buildList {
        if (metrics.spareOpportunities > 0 && metrics.spares * 100.0 / metrics.spareOpportunities < 60) add("dohozy")
        if (metrics.resolvedFrames > 0 && metrics.opens * 100.0 / metrics.resolvedFrames > 25) add("omezení otevřených framů")
        if (metrics.splitSpareOpportunities > 0 && metrics.convertedSplits * 100.0 / metrics.splitSpareOpportunities < 20) add("řešení splitů")
    }
    Div(attrs = { classes("insight-box") }) {
        H4 { Text("Profil hráče") }
        P { Text("Silné stránky: ${strengths.takeIf { it.isNotEmpty() }?.joinToString(", ") ?: "zatím nelze spolehlivě určit"}.") }
        P { Text("Prostor ke zlepšení: ${focus.takeIf { it.isNotEmpty() }?.joinToString(", ") ?: "bez výrazné slabiny v dostupném vzorku"}.") }
    }
}
